import { DropdownMenu as MenuPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';
import { popoverPanelClasses } from './popover';

export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuGroup = MenuPrimitive.Group;
export const DropdownMenuRadioGroup = MenuPrimitive.RadioGroup;

export function DropdownMenuContent({
  children,
  align = 'end',
  className,
  onCloseAutoFocus,
}: {
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
  /**
   * Runs before the focus goes back to the trigger; `preventDefault()` keeps it elsewhere, e.g.
   * in a text the chosen item changed.
   */
  onCloseAutoFocus?: (event: Event) => void;
}) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content
        align={align}
        sideOffset={6}
        collisionPadding={8}
        onCloseAutoFocus={onCloseAutoFocus}
        className={cn(popoverPanelClasses, 'min-w-48 p-1', className)}
      >
        {children}
      </MenuPrimitive.Content>
    </MenuPrimitive.Portal>
  );
}

const itemClasses =
  'relative flex min-h-control cursor-pointer items-center gap-2.5 rounded-sm px-2.5 text-sm outline-none select-none data-disabled:cursor-not-allowed data-disabled:text-fg-disabled data-highlighted:bg-hover';

export interface DropdownMenuItemProps extends ComponentProps<typeof MenuPrimitive.Item> {
  icon?: IconName;
  /** `danger` for destructive actions. They still ask for confirmation afterwards. */
  tone?: 'default' | 'danger';
  /** Keyboard shortcut shown on the right, e.g. `⌘K`. */
  shortcut?: string;
}

export function DropdownMenuItem({
  icon,
  tone = 'default',
  shortcut,
  className,
  children,
  ...props
}: DropdownMenuItemProps) {
  return (
    <MenuPrimitive.Item
      className={cn(
        itemClasses,
        tone === 'danger' ? 'text-danger data-highlighted:bg-danger-subtle' : 'text-fg',
        className,
      )}
      {...props}
    >
      {icon && (
        <Icon name={icon} className={tone === 'danger' ? 'text-danger' : 'text-fg-subtle'} />
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {shortcut && <kbd className="font-sans text-xs text-fg-subtle">{shortcut}</kbd>}
    </MenuPrimitive.Item>
  );
}

export function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: ComponentProps<typeof MenuPrimitive.RadioItem>) {
  return (
    <MenuPrimitive.RadioItem className={cn(itemClasses, 'pr-8 text-fg', className)} {...props}>
      {children}
      <MenuPrimitive.ItemIndicator className="absolute right-2.5 flex">
        <Icon name="check" className="text-brand" />
      </MenuPrimitive.ItemIndicator>
    </MenuPrimitive.RadioItem>
  );
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof MenuPrimitive.CheckboxItem>) {
  return (
    <MenuPrimitive.CheckboxItem className={cn(itemClasses, 'pr-8 text-fg', className)} {...props}>
      {children}
      <MenuPrimitive.ItemIndicator className="absolute right-2.5 flex">
        <Icon name="check" className="text-brand" />
      </MenuPrimitive.ItemIndicator>
    </MenuPrimitive.CheckboxItem>
  );
}

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return (
    <MenuPrimitive.Label className="px-2.5 pt-2 pb-1 text-xs font-medium text-fg-subtle">
      {children}
    </MenuPrimitive.Label>
  );
}

export function DropdownMenuSeparator() {
  return <MenuPrimitive.Separator className="-mx-1 my-1 h-px bg-line" />;
}
